import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import partition from 'lodash/partition';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { CreateSelectionTemplate, NodeSelectionTemplate } from '@cast/types';

import { getClustersNodeTemplatesQueryKey } from 'hooks/queries/autoscaler/useClustersNodeTemplatesQuery';
import { getSelectionTemplatesQueryKey } from 'hooks/queries/autoscaler/useSelectionTemplatesQuery';

type UseCreateSelectionTemplatesMutationArgs = {
  id: string;
  onSuccess?: () => void;
};

export const useCreateSelectionTemplatesMutation = ({
  id,
  onSuccess,
}: UseCreateSelectionTemplatesMutationArgs): UseMutationResult<
  PromiseSettledResult<AxiosResponse<NodeSelectionTemplate>>[],
  unknown,
  CreateSelectionTemplate[],
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templates) =>
      Promise.allSettled(
        templates.map((template) =>
          apiClient.autoscaler.createSelectionTemplate({
            clusterId: id,
            nodeTemplate: template,
          })
        )
      ),
    onSuccess: (data) => {
      const [success, failed] = partition(
        data,
        (entry) => entry.status === 'fulfilled'
      );

      if (success.length === data.length) {
        notify.success(
          `${success.length} ${pluralize(
            'template',
            success.length
          )} have been added`
        );
      }

      if (failed.length === data.length) {
        notify.error(
          `Unable to add ${failed.length} ${pluralize(
            'template',
            failed.length
          )}`
        );
      }

      if (success.length && failed.length) {
        notify.success(
          `${success.length} / ${data.length} ${pluralize(
            'template',
            data.length
          )} have been added`
        );
      }
      queryClient.invalidateQueries({
        queryKey: getSelectionTemplatesQueryKey(id),
      });
      queryClient.invalidateQueries({
        queryKey: getClustersNodeTemplatesQueryKey(),
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error));
    },
  });
};
